// src/pages/InboxPage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { fromZonedTime } from 'date-fns-tz';
import { Button } from '../ui';
import ReservationDrawer from '../components/ReservationDrawer';
import { useCountry } from '../context/CountryContext';
import { useInboxBadge } from '../App';
import { parseWebsiteMessageToPrefill } from '../utils/parseWebsiteLead';

const client = createAuditedClient('InboxPage');

/* ======================= GraphQL ======================= */
// ----- Emails
const EmailsByCompanyByReceived_DT = /* GraphQL */ `
  query EmailsByCompanyByReceived(
    $CompanyId: String!
    $receivedAt: ModelAWSDateTimeKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    emailsByCompanyByReceived(
      CompanyId: $CompanyId
      receivedAt: $receivedAt
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id CompanyId senderEmail subject snippet bodyHtml bodyText
        acceptUrl declineUrl receivedAt isRead dismissedAt
        assignedCarId assignedBySub assignedAt
        gmailMessageId gmailThreadId platform
      }
      nextToken
    }
  }
`;

const EmailsByCompanyByReceived_STR = /* GraphQL */ `
  query EmailsByCompanyByReceived(
    $CompanyId: String!
    $receivedAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    emailsByCompanyByReceived(
      CompanyId: $CompanyId
      receivedAt: $receivedAt
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id CompanyId senderEmail subject snippet bodyHtml bodyText
        acceptUrl declineUrl receivedAt isRead dismissedAt
        assignedCarId assignedBySub assignedAt
        gmailMessageId gmailThreadId platform
      }
      nextToken
    }
  }
`;

// ----- Inbound requests (landing page)
const InboundByCompanyByCreatedAt = /* GraphQL */ `
  query InboundByCompanyByCreatedAt(
    $CompanyId: String!
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    inboundByCompanyByCreatedAt(
      CompanyId: $CompanyId
      createdAt: $createdAt
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        CompanyId
        type
        status

        name
        email
        phone
        whatsapp
        country

        start
        end
        pickupLocation
        dropoffLocation
        transmissionPref
        categoryPref

        selectedCarId
        selectedCarName
        selectedCarPlate
        selectedCarMeta

        message
        payload
        source

        assignedCarId
        assignedBySub
        assignedAt

        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const UpdateInboundRequest = /* GraphQL */ `
  mutation UpdateInboundRequest($input: UpdateInboundRequestInput!) {
    updateInboundRequest(input: $input) {
      id
      status
      assignedCarId
      assignedBySub
      assignedAt
      updatedAt
    }
  }
`;

// ----- Cars / Companies
const ListCars = /* GraphQL */ `
  query ListCars {
    listCars(limit: 1000) {
      items { id name plate CompanyId active }
      nextToken
    }
  }
`;
const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items {
        id
        name
        country
        currencies
        pickupLocations
        dropoffLocations
      }
      nextToken
    }
  }
`;

// ----- Mutations for reservation + email linking
const AssignEmailToCar = /* GraphQL */ `
  mutation AssignEmailToCar($emailId: ID!, $carId: ID!) {
    assignEmailToCar(emailId: $emailId, carId: $carId) {
      id
      assignedCarId
      assignedAt
      assignedBySub
    }
  }
`;
const UpdateEmailMessage = /* GraphQL */ `
  mutation UpdateEmailMessage($input: UpdateEmailMessageInput!) {
    updateEmailMessage(input: $input) {
      id
      isRead
      assignedCarId
      assignedBySub
      assignedAt
      platform
    }
  }
`;

const RunEmailIngest = /* GraphQL */ `
  mutation RunEmailIngest {
    runEmailIngest {
      ok
      message
    }
  }
`;

/* ======================= Helpers ======================= */
const EXTRA_DRIVERS_PREFIX = 'extra_drivers:';
const MAX_EXTRA_DRIVERS = 10;
const MAX_SEAT_EXTRA_COUNT = 3;
const EXTRA_TOKEN_ALIASES = {
  boosterseat: 'booster_seat',
  infantseat: 'infant_seat',
  babyseat: 'baby_seat',
  babycarseat: 'baby_seat',
  snowchains: 'snow_chains',
  gps: 'gps',
  bluetoothreceiver: 'bluetooth_receiver',
  crossborder: 'cross_border',
};
const SEAT_EXTRA_DEFS = [
  { token: 'booster_seat', keys: ['boosterSeat', 'booster_seat'], label: 'Booster seat' },
  { token: 'infant_seat', keys: ['infantSeat', 'infant_seat'], label: 'Infant seat' },
  { token: 'baby_seat', keys: ['babySeat', 'baby_seat', 'babyCarSeat', 'baby_car_seat'], label: 'Baby seat' },
];
const CROSS_BORDER_COUNTRY_OPTIONS = ['Kosovo', 'Greece', 'North Macedonia', 'Montenegro'];
const CROSS_BORDER_COUNTRY_TOKEN_PREFIX = 'cross_border_country:';

function normalizeCrossBorderCountryKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function normalizeCrossBorderCountries(value) {
  const byKey = CROSS_BORDER_COUNTRY_OPTIONS.reduce((acc, country) => {
    acc[normalizeCrossBorderCountryKey(country)] = country;
    return acc;
  }, {});
  const raw = [];
  const add = (item) => {
    if (Array.isArray(item)) {
      item.forEach(add);
      return;
    }
    const s = String(item || '').trim();
    if (!s) return;
    if (s.includes(',')) {
      s.split(',').forEach(add);
      return;
    }
    raw.push(s);
  };

  add(value);

  const out = [];
  for (const item of raw) {
    const clean = item.replace(/^cross[\s_-]*border[\s_-]*country\s*[:=]\s*/i, '');
    const country = byKey[normalizeCrossBorderCountryKey(clean)];
    if (country && !out.includes(country)) out.push(country);
  }
  return out;
}

function crossBorderCountryToken(country) {
  const normalized = normalizeCrossBorderCountries(country)[0];
  return normalized ? `${CROSS_BORDER_COUNTRY_TOKEN_PREFIX}${normalized.toLowerCase().replace(/\s+/g, '_')}` : '';
}

function normalizeExtraDriversCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_EXTRA_DRIVERS, Math.floor(n)));
}

function normalizeSeatExtraCount(value, selectedFallback = false) {
  const n = Number(value);
  if (Number.isFinite(n)) return Math.max(0, Math.min(MAX_SEAT_EXTRA_COUNT, Math.floor(n)));
  return selectedFallback ? 1 : 0;
}

function normalizeExtraKeyForMatch(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function seatExtraDefForKey(key) {
  const normalized = normalizeExtraKeyForMatch(key);
  return SEAT_EXTRA_DEFS.find((def) => {
    const aliases = [
      def.token,
      ...def.keys,
      ...def.keys.flatMap((baseKey) => [`${baseKey}Count`, `${baseKey}Quantity`, `${baseKey}Qty`, `${baseKey}s`]),
    ];
    return aliases.some((alias) => normalizeExtraKeyForMatch(alias) === normalized);
  }) || null;
}

function parseSeatExtraCountToken(raw) {
  const token = String(raw || '').trim();
  if (!token) return null;
  const match = token.match(/^([a-zA-Z][a-zA-Z0-9_\s-]*?)\s*[:=]\s*(\d+)$/);
  if (!match) return null;
  const def = seatExtraDefForKey(match[1]);
  if (!def) return null;
  return { token: def.token, count: normalizeSeatExtraCount(match[2]) };
}

function seatExtraToken(token, count) {
  const normalizedCount = normalizeSeatExtraCount(count);
  if (normalizedCount <= 0) return '';
  return normalizedCount === 1 ? token : `${token}:${normalizedCount}`;
}

function parseExtraDriversCountToken(raw) {
  const token = String(raw || '').trim().toLowerCase();
  if (!token) return null;

  if (token === 'extra_driver' || token === 'extra_drivers' || token === 'extradriver' || token === 'extradrivers') {
    return 1;
  }

  if (token.startsWith(EXTRA_DRIVERS_PREFIX)) {
    return normalizeExtraDriversCount(token.slice(EXTRA_DRIVERS_PREFIX.length));
  }

  const match = token.match(/^extra[\s_-]*drivers?\s*[:=]\s*(\d+)$/i);
  if (match) return normalizeExtraDriversCount(match[1]);

  return null;
}

function normalizeExtraToken(raw) {
  const token = String(raw || '').trim().toLowerCase();
  if (!token) return null;
  return EXTRA_TOKEN_ALIASES[token] || EXTRA_TOKEN_ALIASES[token.replace(/[\s_-]+/g, '')] || null;
}

function isExplicitExtraSelection(value) {
  if (value === true) return true;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' ||
      normalized === 'yes' ||
      normalized === '1' ||
      normalized === 'selected' ||
      normalized === 'included'
    );
  }
  return false;
}

function formatExtraToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return '';

  const seatCount = parseSeatExtraCountToken(raw);
  if (seatCount?.count > 0) {
    const def = SEAT_EXTRA_DEFS.find((item) => item.token === seatCount.token);
    const label = def?.label || formatExtraToken(seatCount.token);
    return seatCount.count > 1 ? `${label} (${seatCount.count})` : label;
  }

  const extraDriversCount = parseExtraDriversCountToken(raw);
  if (extraDriversCount != null && extraDriversCount > 0) {
    return `Extra drivers: ${extraDriversCount}`;
  }

  const normalized = normalizeExtraToken(raw) || raw.toLowerCase();

  if (normalized.startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)) {
    const countries = normalizeCrossBorderCountries(normalized);
    return countries.length ? `Cross-border country: ${countries.join(', ')}` : '';
  }
  if (normalized === 'booster_seat') return 'Booster seat';
  if (normalized === 'infant_seat') return 'Infant seat';
  if (normalized === 'baby_seat') return 'Baby seat';
  if (normalized === 'snow_chains') return 'Snow chains';
  if (normalized === 'gps') return 'GPS';
  if (normalized === 'bluetooth_receiver') return 'Bluetooth receiver';
  if (normalized === 'cross_border') return 'Cross-border permit';

  return raw;
}

function mapInboundExtrasToReservationExtras(extrasInput) {
  if (!extrasInput) return [];

  const out = [];
  let extraDriversCount = 0;
  const seatCounts = {};

  const push = (value) => {
    const token = String(value || '').trim().toLowerCase();
    if (!token) return;
    if (!out.includes(token)) out.push(token);
  };

  const ingestObject = (obj) => {
    for (const [k, v] of Object.entries(obj || {})) {
      const key = String(k || '').trim();
      const lower = key.toLowerCase();
      const seatDef = seatExtraDefForKey(key);
      if (seatDef) {
        const isCountField = /count|quantity|qty|seats?$/i.test(key);
        const count = isCountField
          ? normalizeSeatExtraCount(v)
          : normalizeSeatExtraCount(null, isExplicitExtraSelection(v));
        seatCounts[seatDef.token] = Math.max(seatCounts[seatDef.token] || 0, count);
        continue;
      }

      if (
        lower === 'extradriverscount' ||
        lower === 'extra_drivers_count' ||
        lower === 'extra_driver_count'
      ) {
        extraDriversCount = Math.max(extraDriversCount, normalizeExtraDriversCount(v));
        continue;
      }

      if (lower === 'extradriver' || lower === 'extra_driver' || lower === 'extra_drivers') {
        if (v) extraDriversCount = Math.max(extraDriversCount, 1);
        continue;
      }

      if (
        lower === 'crossbordercountries' ||
        lower === 'cross_border_countries' ||
        lower === 'crossbordercountry' ||
        lower === 'cross_border_country' ||
        lower === 'crossborderdestinationcountries'
      ) {
        const countries = normalizeCrossBorderCountries(v);
        if (countries.length) {
          push('cross_border');
          countries.forEach((country) => push(crossBorderCountryToken(country)));
        }
        continue;
      }

      if (!isExplicitExtraSelection(v)) continue;
      const mapped = normalizeOne(key);
      if (mapped) push(mapped);
    }
  };

  const normalizeOne = (x) => {
    if (!x) return null;

    if (typeof x === 'string') {
      const token = x.trim().toLowerCase();
      const parsedSeatCount = parseSeatExtraCountToken(token);
      if (parsedSeatCount) {
        seatCounts[parsedSeatCount.token] = Math.max(seatCounts[parsedSeatCount.token] || 0, parsedSeatCount.count);
        return null;
      }
      const seatDef = seatExtraDefForKey(token);
      if (seatDef && normalizeExtraToken(token) === seatDef.token) {
        seatCounts[seatDef.token] = Math.max(seatCounts[seatDef.token] || 0, 1);
        return null;
      }
      if (token.startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX) || /^cross[\s_-]*border[\s_-]*country\s*[:=]/i.test(token)) {
        return crossBorderCountryToken(token);
      }
      return normalizeExtraToken(x);
    }

    if (typeof x === 'object') {
      const raw = x.value ?? x.key ?? x.id ?? x.name ?? x.code ?? null;
      if (raw == null) return null;
      return normalizeOne(String(raw));
    }

    return null;
  };

  if (Array.isArray(extrasInput)) {
    for (const item of extrasInput) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const mapped = normalizeOne(item);
        if (mapped) {
          push(mapped);
          continue;
        }
        ingestObject(item);
        continue;
      }

      const parsedCount = parseExtraDriversCountToken(item);
      if (parsedCount != null) {
        extraDriversCount = Math.max(extraDriversCount, parsedCount);
        continue;
      }

      const mapped = normalizeOne(item);
      if (mapped) push(mapped);
    }
  } else if (typeof extrasInput === 'string') {
    const parsed = safeJsonParseDeep(extrasInput, 2);
    if (parsed && typeof parsed === 'object') {
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          const parsedCount = parseExtraDriversCountToken(item);
          if (parsedCount != null) {
            extraDriversCount = Math.max(extraDriversCount, parsedCount);
            continue;
          }

          if (item && typeof item === 'object' && !Array.isArray(item)) {
            const mapped = normalizeOne(item);
            if (mapped) {
              push(mapped);
              continue;
            }
            ingestObject(item);
            continue;
          }

          const mapped = normalizeOne(item);
          if (mapped) push(mapped);
        }
      } else {
        ingestObject(parsed);
      }
    } else {
      for (const segment of extrasInput.split(/[,\n]/g)) {
        const parsedCount = parseExtraDriversCountToken(segment);
        if (parsedCount != null) {
          extraDriversCount = Math.max(extraDriversCount, parsedCount);
          continue;
        }

        const mapped = normalizeOne(segment);
        if (mapped) push(mapped);
      }
    }
  } else if (typeof extrasInput === 'object') {
    ingestObject(extrasInput);
  }

  if (extraDriversCount > 0) {
    push(`${EXTRA_DRIVERS_PREFIX}${extraDriversCount}`);
  }
  for (const def of SEAT_EXTRA_DEFS) {
    const token = seatExtraToken(def.token, seatCounts[def.token] || 0);
    if (token) push(token);
  }

  return out;
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}
// ======================= UI filter (match Gmail query) =======================

// Match the same senders you have in INBOX_CONFIG / GMAIL_QUERY
const RESERVATION_SENDERS = new Set([
  'confirmations@bookinggroup.com',
  'support@discovercars.com',
  'mail@localrent.com',
]);

// Match the same subject tokens you use in the Gmail query
const RESERVATION_SUBJECT_TOKENS = ['reservation', 'booking', 'confirmation', 'order'];

function extractEmailAddress(fromLike) {
  const s = String(fromLike || '').trim();
  const m = s.match(/<([^>]+)>/);
  return (m?.[1] || s).trim().toLowerCase();
}

function shouldShowEmailInInbox(email) {
  // Only apply to ingested Gmail emails, not inbound items
  const sender = extractEmailAddress(email?.senderEmail);
  const subject = String(email?.subject || '').toLowerCase();

  const senderOk = RESERVATION_SENDERS.has(sender);
  const subjectOk = RESERVATION_SUBJECT_TOKENS.some((t) => subject.includes(t));

  return senderOk && subjectOk;
}

function safeJsonParseDeep(value, maxDepth = 3) {
  if (value == null) return null;

  let v = value;
  for (let i = 0; i < maxDepth; i++) {
    if (typeof v === 'object') return v;
    if (typeof v !== 'string') return null;

    const s = v.trim();
    if (!s) return null;

    try {
      v = JSON.parse(s);
    } catch {
      return null;
    }
  }

  return typeof v === 'object' ? v : null;
}

function asFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const n = asFiniteNumber(value);
    if (n != null) return n;
  }
  return null;
}

function normalizePaymentStatusForReservation(value) {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return 'NOT_PAID';
  if (raw === 'PARTIALLY_PAID' || raw === 'PARTIAL') return 'PARTIALLY_PAID';
  if (
    raw === 'PREPAID' ||
    raw === 'PAID' ||
    raw === 'CAPTURED' ||
    raw === 'SETTLED' ||
    raw === 'SUCCESS' ||
    raw === 'VERIFIED'
  ) {
    return 'PREPAID';
  }
  return 'NOT_PAID';
}

function extractInboundPayloadPrefill(payloadObjRaw) {
  const payloadObj = payloadObjRaw && typeof payloadObjRaw === 'object' ? payloadObjRaw : {};
  const pricing = payloadObj?.pricing && typeof payloadObj.pricing === 'object' ? payloadObj.pricing : {};
  const payment = payloadObj?.payment && typeof payloadObj.payment === 'object' ? payloadObj.payment : {};

  const parsedBreakdown =
    pricing?.breakdown && typeof pricing.breakdown === 'object'
      ? pricing.breakdown
      : safeJsonParseDeep(pricing?.breakdown, 2) || {};
  const addons =
    parsedBreakdown?.addons && typeof parsedBreakdown.addons === 'object'
      ? parsedBreakdown.addons
      : {};
  const insuranceLine =
    addons?.lines?.insurance && typeof addons.lines.insurance === 'object'
      ? addons.lines.insurance
      : {};

  const wantInsurance = Boolean(payloadObj?.wantInsurance || addons?.wantInsurance);
  const authoritativeCurrency =
    normalizeCurrencyCode(
      pricing?.payableNowCurrency ||
      pricing?.paidCurrency ||
      payment?.currency ||
      pricing?.currency ||
      parsedBreakdown?.currency ||
      ''
    ) || '';
  const rentalCurrency = authoritativeCurrency;
  const breakdownCurrency = normalizeCurrencyCode(parsedBreakdown?.currency || '');
  const canTrustBreakdown = !!Object.keys(parsedBreakdown).length && (!breakdownCurrency || breakdownCurrency === rentalCurrency);
  const addonsTotalValue = firstFiniteNumber(pricing?.addonsTotal);

  let rentalAmount = firstFiniteNumber(pricing?.rentalTotal);
  let insuranceAmount = wantInsurance
    ? firstFiniteNumber(pricing?.insuranceFeeTotal, pricing?.insuranceAmount)
    : null;
  let extrasAmount = firstFiniteNumber(pricing?.extrasFeeTotal);
  let quoteTotal = firstFiniteNumber(pricing?.total, pricing?.estimatedTotal);

  if (quoteTotal != null && rentalAmount == null && addonsTotalValue != null) {
    rentalAmount = Math.max(0, quoteTotal - addonsTotalValue);
  }
  if (extrasAmount == null && addonsTotalValue != null) {
    extrasAmount = Math.max(0, addonsTotalValue - Number(insuranceAmount || 0));
  }

  if (canTrustBreakdown) {
    const breakdownAddonsTotal = firstFiniteNumber(parsedBreakdown?.addons?.total);
    const breakdownInsuranceTotal = wantInsurance
      ? firstFiniteNumber(insuranceLine?.total, insuranceLine?.amount, addons?.insuranceTotal)
      : null;

    rentalAmount = firstFiniteNumber(parsedBreakdown?.baseTotal, rentalAmount);
    insuranceAmount = breakdownInsuranceTotal != null ? breakdownInsuranceTotal : insuranceAmount;
    extrasAmount =
      breakdownAddonsTotal != null
        ? Math.max(0, breakdownAddonsTotal - Number(breakdownInsuranceTotal || 0))
        : extrasAmount;
    quoteTotal = firstFiniteNumber(parsedBreakdown?.total, quoteTotal);
  }

  const depositAmount = firstFiniteNumber(pricing?.policyDepositAmount, pricing?.depositAmount);
  const depositCurrency =
    normalizeCurrencyCode(
      pricing?.policyDepositCurrency || pricing?.depositCurrency || rentalCurrency || ''
    ) || '';

  let paymentStatus = normalizePaymentStatusForReservation(
    pricing?.paymentStatus || payment?.status || payment?.capture?.rawStatus
  );

  const amountPaidDirect = firstFiniteNumber(
    pricing?.paidAmount,
    payment?.amount,
    payment?.amountUsd,
    payment?.capture?.amount
  );
  const payableNow = firstFiniteNumber(pricing?.payableNow, payment?.amountUsd);

  let amountPaid = amountPaidDirect;
  if (amountPaid == null && paymentStatus !== 'NOT_PAID') {
    amountPaid = payableNow;
  }
  if (amountPaid != null && amountPaid <= 0) {
    amountPaid = null;
  }
  if (paymentStatus === 'PREPAID') {
    const isFullyPaid =
      amountPaid != null &&
      rentalAmount != null &&
      amountPaid >= rentalAmount - 0.01;
    if (!isFullyPaid) {
      paymentStatus = 'NOT_PAID';
    }
  }

  const insuranceCurrency = wantInsurance
    ? authoritativeCurrency
    : '';

  return {
    rentalAmount,
    rentalCurrency,
    depositAmount,
    depositCurrency,
    paymentStatus,
    amountPaid,
    extrasAmount,
    quoteTotal,
    insuranceIncluded: wantInsurance ? 'INCLUDED' : 'NOT_INCLUDED',
    insuranceAmount: insuranceAmount != null && insuranceAmount > 0 ? insuranceAmount : null,
    insuranceCurrency: insuranceCurrency || '',
  };
}

function formatPrefillMoney(amount, currency) {
  const n = asFiniteNumber(amount);
  if (n == null) return '';
  const normalizedCurrency = normalizeCurrencyCode(currency || '');
  const rounded = Math.round(n * 100) / 100;
  const value = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return normalizedCurrency ? `${value} ${normalizedCurrency}` : value;
}

function formatInsuranceCoverageStatus(value) {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'INCLUDED') return 'Included';
  if (normalized === 'NOT_INCLUDED') return 'Not included';
  return '';
}

function formatReservationPaymentStatus(value) {
  const normalized = normalizePaymentStatusForReservation(value);
  if (normalized === 'PREPAID') return 'Prepaid';
  if (normalized === 'PARTIALLY_PAID') return 'Partially paid';
  return 'Not paid';
}

function formatExtras(extras) {
  if (!extras) return '';

  const mapped = mapInboundExtrasToReservationExtras(extras);
  if (mapped.length) {
    const countries = [];
    for (const token of mapped) {
      if (!String(token || '').toLowerCase().startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)) continue;
      for (const country of normalizeCrossBorderCountries(token)) {
        if (!countries.includes(country)) countries.push(country);
      }
    }
    const visible = mapped.filter((token) => {
      const t = String(token || '').toLowerCase();
      if (t.startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)) return false;
      if (t === 'cross_border' && countries.length) return false;
      return true;
    });
    const labels = visible.map((token) => formatExtraToken(token)).filter(Boolean);
    if (countries.length) labels.push(`Cross-border permit (${countries.join(', ')})`);
    return labels.join(', ');
  }

  if (typeof extras === 'string') {
    const trimmed = extras.trim();
    if (!trimmed) return '';
    const parsed = safeJsonParseDeep(trimmed, 2);
    return parsed && typeof parsed === 'object' ? '' : trimmed;
  }

  if (typeof extras === 'object') return '';
  return String(extras || '');
}

function inferTimeZoneForCompany(company, fallback = null) {
  // Hard override if you ever add a field later:
  if (company?.timeZone) return company.timeZone;
  if (company?.timezone) return company.timezone;
  if (company?.tz) return company.tz;

  const country = String(company?.country || '').trim().toUpperCase();
  if (country === 'GE' || country === 'GEO' || country === 'GEORGIA') return 'Asia/Tbilisi';
  if (country === 'AL' || country === 'ALB' || country === 'ALBANIA') return 'Europe/Tirane';
  if (country === 'LB' || country === 'LBN' || country === 'LEBANON') return 'Asia/Beirut';

  const cur = Array.isArray(company?.currencies) ? company.currencies.join(',').toUpperCase() : '';

  // Heuristics based on your deployments
  if (cur.includes('GEL')) return 'Asia/Tbilisi';     // Georgia
  if (cur.includes('ALL')) return 'Europe/Tirane';    // Albania
  if (cur.includes('LBP')) return 'Asia/Beirut';      // Lebanon

  // Stable fallback (never viewer-local)
  return fallback || 'UTC';
}

function asWallClockInTimeZoneToIso(value, timeZone) {
  if (!value) return null;

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const pad = (n) => String(n).padStart(2, '0');
  const localWallClock = [
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  ].join('T');

  const utcDate = fromZonedTime(localWallClock, timeZone || 'UTC');
  return Number.isNaN(utcDate.getTime()) ? null : utcDate.toISOString();
}

function formatDateTimeDMY(iso, timeZone) {
  if (!iso) return '';
  const s = String(iso).trim();
  if (!s) return '';
  const hasTZ = /([zZ]|[+\-]\d{2}:\d{2})$/.test(s);
  const d = hasTZ ? new Date(s) : fromZonedTime(s, timeZone || 'UTC');
  if (Number.isNaN(d.getTime())) return String(iso);

  try {
    // en-GB => dd/mm/yyyy formatting
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone || 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).format(d);
  } catch {
    // If an invalid TZ string is passed, use UTC rendering (stable across viewers)
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min} UTC`;
  }
}

/* ================ Mobile detection hook ================= */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);

  return isMobile;
}

/* ======================= Email parsing helpers ======================= */

function normalize(text) {
  return (text || '').replace(/\s+/g, ' ').trim();
}

function decodeHtmlEntities(s = '') {
  if (!s) return '';
  let out = s.replaceAll('&quot;', '"').replaceAll('&#34;', '"').replaceAll('&amp;', '&');
  if (typeof window !== 'undefined' && window.DOMParser) {
    try {
      const doc = new DOMParser().parseFromString(out, 'text/html');
      out = doc.documentElement.textContent || out;
    } catch { }
  }
  return out;
}

// Better HTML -> text: preserve line breaks for parsers.
function stripHtml(html) {
  if (!html) return '';
  const decoded = decodeHtmlEntities(html);

  // preserve common structure newlines
  const withBreaks = decoded
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n')
    .replace(/<\/\s*div\s*>/gi, '\n')
    .replace(/<\/\s*tr\s*>/gi, '\n')
    .replace(/<\/\s*li\s*>/gi, '\n');

  // remove tags
  const text = withBreaks.replace(/<[^>]+>/g, ' ');
  // normalize but keep newlines meaningful
  return text
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Extract all text from HTML tables into "Label: Value" lines (helps a lot).
function htmlTablesToKVText(html) {
  if (!html || typeof window === 'undefined' || !window.DOMParser) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const rows = Array.from(doc.querySelectorAll('table tr'));
    const lines = [];

    for (const tr of rows) {
      const cells = Array.from(tr.querySelectorAll('th,td')).map((c) => normalize(c.textContent));
      if (cells.length >= 2) {
        const k = cells[0];
        const v = cells.slice(1).join(' ').trim();
        if (k && v) lines.push(`${k}: ${v}`);
      }
    }
    return lines.join('\n');
  } catch {
    return '';
  }
}

function detectSource(email) {
  const from = (email?.senderEmail || '').toLowerCase();
  const subject = (email?.subject || '').toLowerCase();

  // include HTML-derived text as well
  const bodyText = (
    email?.bodyText ||
    htmlTablesToKVText(email?.bodyHtml || '') ||
    stripHtml(email?.bodyHtml || '')
  ).toLowerCase();

  // ========== DiscoverCars ==========
  if (
    from.includes('discovercars') ||
    from.includes('discover-cars') ||
    from.includes('discovercars.com') ||
    subject.includes('discovercars') ||
    subject.includes('discover cars') ||
    subject.includes('discovercars.com') ||
    bodyText.includes('discover cars booking number') ||
    bodyText.includes('discovercars booking number')
  ) {
    return 'discovercars';
  }

  // ========== Booking Group / EconomyBookings ==========
  if (
    from.includes('bookinggroup') ||
    from.includes('booking-group') ||
    from.includes('bookinggroup.com') ||
    from.includes('economybookings') ||
    from.includes('economybookings.com') ||
    subject.includes('booking group') ||
    subject.includes('bookinggroup') ||
    subject.includes('economybookings') ||
    bodyText.includes('booking group') ||
    bodyText.includes('bookinggroup.com') ||
    bodyText.includes('our ref:')
  ) {
    return 'economybookings';
  }

  // ========== Localrent ==========
  if (
    from.includes('localrent') ||
    from.includes('local-rent') ||
    from.includes('localrent.com') ||
    subject.includes('localrent') ||
    subject.includes('local rent') ||
    bodyText.includes('localrent.com') ||
    (bodyText.includes('new reservation') && bodyText.includes('localrent'))
  ) {
    return 'local_rent';
  }

  return 'other';
}

function pickEmailText(email) {
  // Prefer bodyText, but supplement from HTML tables and stripped HTML.
  const a = (email?.bodyText || '').trim();
  const b = htmlTablesToKVText(email?.bodyHtml || '').trim();
  const c = stripHtml(email?.bodyHtml || '').trim();

  // If bodyText is short/empty, use the richest.
  const candidates = [a, b, c].filter(Boolean).sort((x, y) => y.length - x.length);
  return candidates[0] || '';
}

// ---------- date parsing (more formats) ----------
// 1) REPLACE parseLooseDateTime with this version (adds DD-Mon-YYYY, H:MM format)

const MONTH_MAP = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

function parseLooseDateTime(str) {
  if (!str) return null;
  const cleaned = String(str).replace(/\s+/g, ' ').trim();
  if (!cleaned) return null;

  // A) "20-Apr-2026, 9:00" or "03-Dec-1970" (optional time)
  {
    const m = cleaned.match(
      /(\d{1,2})-([A-Za-z]{3,})-(\d{4})(?:,?\s*(\d{1,2}):(\d{2}))?/ // time optional
    );
    if (m) {
      const [, ddStr, monStr, yyyyStr, hhStr, mmStr] = m;
      const day = parseInt(ddStr, 10);
      const year = parseInt(yyyyStr, 10);
      const monKey = monStr.slice(0, 3).toLowerCase();
      const month = MONTH_MAP[monKey];

      if (month != null) {
        const hour = hhStr != null ? parseInt(hhStr, 10) : 0;
        const minute = mmStr != null ? parseInt(mmStr, 10) : 0;
        const d = new Date(year, month, day, hour, minute);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }
  }
  // B0) "Mar 1, 2026, 22:30" / "March 1, 2026, 5:00" (month first)
  {
    const m = cleaned.match(/([A-Za-z]{3,})\s+(\d{1,2}),\s*(\d{4}),?\s*(\d{1,2}):(\d{2})/);
    if (m) {
      const [, monStr, ddStr, yyyyStr, hhStr, mmStr] = m;
      const day = parseInt(ddStr, 10);
      const year = parseInt(yyyyStr, 10);
      const hour = parseInt(hhStr, 10);
      const minute = parseInt(mmStr, 10);
      const monKey = monStr.slice(0, 3).toLowerCase();
      const month = MONTH_MAP[monKey];
      if (month != null) {
        const d = new Date(year, month, day, hour, minute);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }
  }

  // B) "7 Dec 2025, 23:30" / "07 December 2025, 23:30"
  {
    const m = cleaned.match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4}),?\s*(\d{1,2}):(\d{2})/);
    if (m) {
      const [, ddStr, monStr, yyyyStr, hhStr, mmStr] = m;
      const day = parseInt(ddStr, 10);
      const year = parseInt(yyyyStr, 10);
      const hour = parseInt(hhStr, 10);
      const minute = parseInt(mmStr, 10);
      const monKey = monStr.slice(0, 3).toLowerCase();
      const month = MONTH_MAP[monKey];
      if (month != null) {
        const d = new Date(year, month, day, hour, minute);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }
  }

  // C) "25.12.2025 13:00"
  {
    const m = cleaned.match(/(\d{2})\.(\d{2})\.(\d{4})[,\s]+(\d{1,2}):(\d{2})/);
    if (m) {
      const [, dd, mm, yyyy, hh, min] = m;
      const d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        parseInt(hh, 10),
        parseInt(min, 10)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  // D) "2025-12-25 13:00"
  {
    const m = cleaned.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})/);
    if (m) {
      const [, yyyy, mm, dd, hh, min] = m;
      const d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        parseInt(hh, 10),
        parseInt(min, 10)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  // E) Fallback native
  const d = new Date(cleaned);
  return Number.isNaN(d.getTime()) ? null : d;
}


function extractField(text, label, nextLabels) {
  if (!text) return null;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const labelRe = esc(label);
  const nextRe = nextLabels && nextLabels.length ? nextLabels.map(esc).join('|') : '$^';
  const re = new RegExp(labelRe + '\\s*(.*?)\\s*(?=(' + nextRe + '|$))', 'i');
  const m = text.match(re);
  return m ? normalize(m[1]) : null;
}

function parseMoneyLoose(s) {
  if (!s) return { amount: null, currency: '' };

  // Handles: "6.00 USD", "USD 6.00", "6,00 EUR", "€ 6.00", "130.00 $"
  const txt = normalize(s);

  // currency first
  let m = txt.match(/^([A-Za-z]{3}|[$€£])\s*([0-9]+(?:[.,][0-9]+)?)$/);
  if (m) {
    const cur = normalizeCurrencyCode(m[1]);
    const amt = parseFloat(m[2].replace(',', '.'));
    return { amount: Number.isNaN(amt) ? null : amt, currency: cur };
  }

  // amount first
  m = txt.match(/^([0-9]+(?:[.,][0-9]+)?)\s*([A-Za-z]{3}|[$€£])$/);
  if (m) {
    const amt = parseFloat(m[1].replace(',', '.'));
    const cur = normalizeCurrencyCode(m[2]);
    return { amount: Number.isNaN(amt) ? null : amt, currency: cur };
  }

  return { amount: null, currency: '' };
}
function KV({ k, v }) {
  if (v == null || v === '') return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, padding: '6px 0' }}>
      <div style={{ fontSize: 12, color: 'rgba(15,23,42,.55)', fontWeight: 900 }}>{k}</div>
      <div style={{ fontSize: 13, color: 'rgba(15,23,42,.88)', wordBreak: 'break-word' }}>{String(v)}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 950, color: 'rgba(15,23,42,.80)', letterSpacing: '.02em' }}>
        {title}
      </div>
      <div style={{ marginTop: 8, borderTop: '1px solid rgba(17,24,39,.08)', paddingTop: 8 }}>{children}</div>
    </div>
  );
}

function prettyMaybeJson(s) {
  if (!s) return '';
  if (typeof s !== 'string') return String(s);
  const t = s.trim();
  if (!t) return '';
  try {
    const obj = JSON.parse(t);
    return JSON.stringify(obj, null, 2);
  } catch {
    return s;
  }
}

function normalizeCurrencyCode(raw) {
  if (!raw) return '';
  const c = raw.trim();
  if (c === '$') return 'USD';
  if (c === '€') return 'EUR';
  if (c === '£') return 'GBP';
  return c.toUpperCase();
}

function InboxStyles() {
  const css = `
  :root{
    --ix-bg: #f3f4f6;
    --ix-card: rgba(255,255,255,.85);
    --ix-solid: #ffffff;
    --ix-stroke: rgba(17,24,39,.10);
    --ix-shadow: 0 18px 50px rgba(15,23,42,.10), 0 2px 10px rgba(15,23,42,.06);
    --ix-text: #0f172a;
    --ix-sub: rgba(15,23,42,.62);
    --ix-sub2: rgba(15,23,42,.46);
    --ix-green: #83d466;
    --ix-green2:#6bbf54;
    --ix-red:#ef4444;
    --ix-radius: 18px;
    --ix-radius2: 14px;
    --ix-ease: cubic-bezier(.2,.9,.2,1);
  }

  .IxPage{
    background: var(--ix-bg);
    min-height: calc(100vh - 64px);
  }

  .IxContainer{
    max-width: 1120px;
    margin: 0 auto;
    padding: 18px 12px 36px;
    display: grid;
    gap: 14px;
  }

  .IxHeader{
    display:flex;
    align-items:flex-end;
    justify-content:space-between;
    gap: 12px;
  }
  .IxTitle{
    margin:0;
    font-size: 22px;
    letter-spacing: -.02em;
    color: var(--ix-text);
  }
  .IxDesc{
    margin:4px 0 0;
    font-size: 13px;
    color: var(--ix-sub);
    line-height: 1.45;
  }
  .IxRole{
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .08em;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(17,24,39,.12);
    background: rgba(131,212,102,.12);
    color: rgba(22,101,52,.95);
    font-weight: 900;
    white-space: nowrap;
  }

  .IxCard{
    background: var(--ix-card);
    border: 1px solid var(--ix-stroke);
    border-radius: var(--ix-radius);
    box-shadow: var(--ix-shadow);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    overflow: hidden;
  }

  .IxToolbar{
    padding: 12px;
    display:flex;
    align-items:center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .IxInput{
    flex: 1 1 260px;
    display:flex;
    align-items:center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 14px;
    border: 1px solid rgba(17,24,39,.14);
    background: rgba(255,255,255,.72);
  }
  .IxInput input{
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: var(--ix-text);
  }
  .IxSelect{
    display:flex;
    align-items:center;
    gap: 8px;
    padding: 10px 10px;
    border-radius: 14px;
    border: 1px solid rgba(17,24,39,.14);
    background: rgba(255,255,255,.72);
    font-size: 13px;
    color: var(--ix-text);
  }
  .IxSelect select{
    border:none; outline:none; background: transparent; font-size: 13px; color: var(--ix-text);
  }

  .IxMeta{
    margin-left: auto;
    font-size: 12px;
    color: var(--ix-sub);
    white-space: nowrap;
  }

  .IxListHead{
    padding: 10px 12px;
    border-top: 1px solid rgba(17,24,39,.08);
    border-bottom: 1px solid rgba(17,24,39,.08);
    background: rgba(255,255,255,.72);
    position: sticky;
    top: 0;
    z-index: 5;
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: 10px;
  }

  .IxList{
    display:grid;
    gap: 10px;
    padding: 12px;
  }

  .IxRow{
    border: 1px solid rgba(17,24,39,.10);
    border-radius: 16px;
    background: rgba(255,255,255,.80);
    overflow: hidden;
    transition: transform .18s var(--ix-ease), box-shadow .18s var(--ix-ease), opacity .18s var(--ix-ease);
  }
  .IxRow:hover{
    transform: translateY(-1px);
    box-shadow: 0 18px 40px rgba(15,23,42,.10);
  }
  .IxRowDone{ opacity: .56; }

  .IxRowTop{
    padding: 10px 12px;
    display:flex;
    align-items:flex-start;
    justify-content: space-between;
    gap: 10px;
    cursor: pointer;
  }

  .IxSubject{
    font-weight: 850;
    letter-spacing: -.01em;
    font-size: 14px;
    color: var(--ix-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 62vw;
  }
  .IxSubjectUnread{
    font-weight: 950;
  }

  .IxSubline{
    margin-top: 6px;
    display:flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items:center;
    color: var(--ix-sub);
    font-size: 12px;
  }

  .IxPill{
    display:inline-flex;
    align-items:center;
    gap: 6px;
    padding: 4px 9px;
    border-radius: 999px;
    border: 1px solid rgba(17,24,39,.10);
    background: rgba(15,23,42,.04);
    color: rgba(15,23,42,.78);
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
  }

  .IxActions{
    display:flex;
    align-items:center;
    gap: 8px;
    flex-shrink: 0;
  }

  .IxMiniBtn{
    border: 1px solid rgba(17,24,39,.14);
    background: rgba(255,255,255,.70);
    border-radius: 14px;
    padding: 8px 10px;
    cursor:pointer;
    font-weight: 900;
    transition: transform .18s var(--ix-ease), background .18s var(--ix-ease);
    white-space: nowrap;
  }
  .IxMiniBtn:hover{ transform: translateY(-1px); background: rgba(131,212,102,.10); }
  .IxMiniBtn:active{ transform: translateY(0px) scale(.99); }

  .IxMiniBtnPrimary{
    border-color: rgba(131,212,102,.35);
    background: rgba(131,212,102,.16);
  }

  .IxBody{
    padding: 12px;
    background: rgba(243,244,246,.75);
    border-top: 1px solid rgba(17,24,39,.08);
    animation: IxOpen .18s var(--ix-ease) both;
  }
  @keyframes IxOpen{
    from{ opacity: 0; transform: translateY(-2px); }
    to{ opacity: 1; transform: translateY(0px); }
  }

  .IxContentBox{
    background: rgba(255,255,255,.88);
    border: 1px solid rgba(17,24,39,.12);
    border-radius: 14px;
    padding: 12px;
    max-height: 360px;
    overflow:auto;
  }

  @media (max-width: 640px){
    .IxContainer{ padding: 12px 10px 28px; }
    .IxHeader{ flex-direction: column; align-items:flex-start; }
    .IxSubject{ max-width: 86vw; }
    .IxMeta{ margin-left: 0; }
    .IxRowTop{ flex-direction: column; }
    .IxActions{ width: 100%; justify-content: flex-end; }
  }

  @media (prefers-reduced-motion: reduce){
    .IxRow, .IxRow:hover, .IxMiniBtn, .IxBody{ transition:none !important; animation:none !important; }
  }
  `;
  return <style>{css}</style>;
}


/* ---------------- EconomyBookings / BookingGroup ---------------- */
function parseBookingGroup(email) {
  const text = pickEmailText(email).replace(/\r/g, '');

  // Some variants have slightly different labels; include alternates.
  const ourRef = extractField(text, 'Our Ref:', ['Customer:', 'Customer name:']) || extractField(text, 'Our ref:', ['Customer:']);
  const customer =
    extractField(text, 'Customer:', ['Customer phone number:', 'Customer phone:', 'Phone:']) ||
    extractField(text, 'Customer name:', ['Customer phone number:', 'Customer phone:', 'Phone:']);

  const phoneChunk =
    extractField(text, 'Customer phone number:', ['Pick Up:', 'Pick up:']) ||
    extractField(text, 'Customer phone:', ['Pick Up:', 'Pick up:']) ||
    extractField(text, 'Phone:', ['Pick Up:', 'Pick up:']);

  const pickupLocation = extractField(text, 'Pick Up:', ['Pick Up Date:', 'Pick up date:', 'Pick Up Date/Time:', 'Pick up:']) ||
    extractField(text, 'Pick up:', ['Pick Up Date:', 'Pick up date:', 'Pick up date/time:']);

  const pickupAtStr =
    extractField(text, 'Pick Up Date:', ['Drop Off:', 'Drop off:', 'Drop Off Date:', 'Drop off date:']) ||
    extractField(text, 'Pick up date:', ['Drop Off:', 'Drop off:', 'Drop Off Date:', 'Drop off date:']) ||
    extractField(text, 'Pick Up Date/Time:', ['Drop Off:', 'Drop off:', 'Drop Off Date:', 'Drop off date:']) ||
    extractField(text, 'Pick up date/time:', ['Drop Off:', 'Drop off:', 'Drop off date:', 'Drop Off Date:']);

  const dropoffLocation =
    extractField(text, 'Drop Off:', ['Drop Off Date:', 'Drop off date:', 'Drop Off Date/Time:']) ||
    extractField(text, 'Drop off:', ['Drop Off Date:', 'Drop off date:', 'Drop off date/time:']);

  const dropoffAtStr =
    extractField(text, 'Drop Off Date:', ['Car Class:', 'Vehicle class:', 'Flight No:', 'Flight number:', 'Special Request:']) ||
    extractField(text, 'Drop off date:', ['Car Class:', 'Vehicle class:', 'Flight No:', 'Flight number:', 'Special Request:']) ||
    extractField(text, 'Drop Off Date/Time:', ['Car Class:', 'Vehicle class:', 'Flight No:', 'Flight number:', 'Special Request:']) ||
    extractField(text, 'Drop off date/time:', ['Car Class:', 'Vehicle class:', 'Flight No:', 'Flight number:', 'Special Request:']);

  const flightNumber =
    extractField(text, 'Flight No:', ['Special Request:', 'The sum to invoice Booking Group:']) ||
    extractField(text, 'Flight number:', ['Special Request:', 'The sum to invoice Booking Group:']);

  const invoiceStr =
    extractField(text, 'The sum to invoice Booking Group:', ['+++', 'Click Here', 'confirm this booking', 'Confirm this booking']) ||
    extractField(text, 'Sum to invoice Booking Group:', ['+++', 'Click Here', 'confirm this booking']);

  // phone cleanup: keep digits + optional +
  let phoneNumbers = [];
  if (phoneChunk) {
    const m = phoneChunk.match(/(\+?\d[\d\s\-().]{5,25})/);
    if (m) {
      const cleanedPhone = m[1].replace(/[^\d+]/g, '');
      phoneNumbers = [cleanedPhone];
    }
  }

  const { amount: rentalAmount, currency: rentalCurrency } = parseMoneyLoose(invoiceStr);

  // Accept/decline URLs: also parse anchors by text
  let acceptUrl = email.acceptUrl || '';
  let declineUrl = email.declineUrl || '';
  if (email.bodyHtml && typeof window !== 'undefined' && window.DOMParser) {
    try {
      const doc = new DOMParser().parseFromString(email.bodyHtml, 'text/html');
      const links = Array.from(doc.querySelectorAll('a'));
      for (const a of links) {
        const txt = normalize(a.textContent).toLowerCase();
        const href = a.href || '';
        if (!href) continue;

        if (
          txt.includes('confirm this booking') ||
          txt.includes('confirm booking') ||
          txt.includes('accept') ||
          txt.includes('confirm')
        ) {
          acceptUrl = acceptUrl || href;
        }
        if (
          txt.includes('decline this booking') ||
          txt.includes('decline booking') ||
          txt.includes('decline') ||
          txt.includes('reject')
        ) {
          declineUrl = declineUrl || href;
        }
      }
    } catch {
      // ignore
    }
  }

  const pickupAt = parseLooseDateTime(pickupAtStr);
  const dropoffAt = parseLooseDateTime(dropoffAtStr);

  return {
    platform: 'economybookings',
    reservationRef: ourRef || '',
    customerName: customer || '',
    phoneNumbers,
    pickupLocation: pickupLocation || '',
    dropoffLocation: dropoffLocation || '',
    pickupAt,
    dropoffAt,
    flightNumber: flightNumber || '',
    rentalAmount,
    rentalCurrency,
    acceptUrl,
    declineUrl,
  };
}

/* ---------------- DiscoverCars ---------------- */
function parseDiscoverCars(email) {
  const html = email.bodyHtml || '';
  let rows = [];

  if (html && typeof window !== 'undefined' && window.DOMParser) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // DiscoverCars emails sometimes have nested tables; just grab all rows.
      rows = Array.from(doc.querySelectorAll('table tr'));
    } catch {
      rows = [];
    }
  }

  const normLabel = (s) =>
    normalize(s || '')
      .toLowerCase()
      .replace(/[\u00a0]/g, ' ')
      .replace(/[:\s]+$/g, '')
      .trim();

  const getValueFromRows = (label) => {
    if (!rows.length) return null;
    const target = normLabel(label);
    for (const tr of rows) {
      const tds = tr.querySelectorAll('td,th');
      if (tds.length < 2) continue;
      const k = normLabel(tds[0].textContent);
      if (k === target) return normalize(tds[1].textContent);
    }
    return null;
  };

  const rawText = pickEmailText(email);
  const lines = rawText
    ? rawText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
    : [];

  const getValueFromText = (label) => {
    if (!lines.length) return null;
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('^' + esc(label) + '\\s*[:\\-\\t]*\\s*(.+)$', 'i');
    for (const line of lines) {
      const m = line.match(re);
      if (m && m[1]) return normalize(m[1]);
    }
    return null;
  };

  const getValue = (label, altLabels = []) => {
    const primary = getValueFromRows(label) || getValueFromText(label);
    if (primary) return primary;
    for (const a of altLabels) {
      const v = getValueFromRows(a) || getValueFromText(a);
      if (v) return v;
    }
    return null;
  };

  const bookingNumber = getValue('Discover Cars booking number', ['Booking number', 'Booking No', 'Reservation number']);
  const pickUp = getValue('Pick-up', ['Pick up', 'Pick-up location']);
  const dropOff = getValue('Drop-off', ['Drop off', 'Drop-off location']);
  const supplierGroup = getValue('Supplier vehicle group', ['Supplier group']);
  const vehicleClass = getValue('Vehicle class', ['Vehicle group', 'Car class']);
  const driverName = getValue('Driver name', ['Main driver', 'Driver']);
  const voucherValueStr = getValue('Voucher value', ['Total', 'Rental price']);
  const depositStr = getValue('Deposit', ['Deposit amount']);

  // 2) PATCH parseDiscoverCars: replace only extractDateChunk() + buildLocationAndDate() inside it
  //    (everything else in parseDiscoverCars can stay)
  const extractDateChunk = (text) => {
    if (!text) return null;

    // Supports:
    // - "Mar 1, 2026, 22:30"
    // - "10 Feb 2026, 20:00"
    // - "20-Apr-2026, 9:00"
    // - "20-Apr-2026, 09:00"
    const m =
      // Month-first
      text.match(/([A-Za-z]{3,}\s+\d{1,2},\s*\d{4},\s*\d{1,2}:\d{2})/) ||
      // Day-first
      text.match(/(\d{1,2}\s+[A-Za-z]{3,}\s+\d{4},\s*\d{1,2}:\d{2})/) ||
      // Dash month format
      text.match(/(\d{1,2}-[A-Za-z]{3}-\d{4},\s*\d{1,2}:\d{2})/);

    return m ? m[1] : null;
  };


  const buildLocationAndDate = (raw) => {
    if (!raw) return { location: '', at: null };
    const s = String(raw);

    const dateChunk = extractDateChunk(s);
    let location = s;

    if (dateChunk) {
      const idx = s.indexOf(dateChunk);
      if (idx >= 0) {
        location = s.slice(0, idx);
      }
    }

    // Clean location: remove trailing commas/spaces
    location = location.replace(/\s+/g, ' ').replace(/[,;\s]+$/g, '').trim();

    const at = parseLooseDateTime(dateChunk);
    return { location, at };
  };


  const pickupParsed = buildLocationAndDate(pickUp);
  const dropoffParsed = buildLocationAndDate(dropOff);

  const voucher = parseMoneyLoose(voucherValueStr);
  const deposit = parseMoneyLoose(depositStr);

  return {
    platform: 'discovercars',
    reservationRef: bookingNumber || '',
    customerName: driverName || '',
    pickupLocation: pickupParsed.location || '',
    dropoffLocation: dropoffParsed.location || '',
    pickupAt: pickupParsed.at,
    dropoffAt: dropoffParsed.at,
    rentalAmount: voucher.amount,
    rentalCurrency: voucher.currency,
    depositAmount: deposit.amount,
    depositCurrency: deposit.currency,
    acceptUrl: email.acceptUrl || '',
    declineUrl: email.declineUrl || '',
    carGroup: supplierGroup || '',
    carClass: vehicleClass || '',
  };
}

/* ---------------- Localrent ---------------- */
function parseNumberSmart(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;

  // remove spaces
  s = s.replace(/\s+/g, '');

  // 1) 1.234,56  (dot thousands, comma decimals)
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) {
    s = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
  }

  // 2) 1,234.56  (comma thousands, dot decimals)
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) {
    s = s.replace(/,/g, '');
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
  }

  // 3) fallback: treat comma as decimal separator
  const n = parseFloat(s.replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

/* ---------------- Localrent ---------------- */
function parseLocalRent(email) {
  const text = pickEmailText(email).replace(/\r/g, '');

  const reservationMatch = text.match(/New reservation\s+(\d+)/i);
  const reservationRef = reservationMatch ? reservationMatch[1] : '';

  let pickupLocation = '';
  let pickupAt = null;

  const pickupMatch = text.match(/Pickup\s+(\d{2}\.\d{2}\.\d{4})\s+(.+?)\s+(\d{1,2}:\d{2})/i);
  if (pickupMatch) {
    const [, dateStr, locRaw, timeStr] = pickupMatch;
    const loc = locRaw.replace(/\s*(Rental office|Office|Airport)\s*$/i, '').trim();
    pickupLocation = normalize(loc.replace(/[,;]\s*$/, ''));
    const [dd, mm, yyyy] = dateStr.split('.');
    const iso = `${yyyy}-${mm}-${dd}T${timeStr}:00`;
    const d = new Date(iso);
    pickupAt = Number.isNaN(d.getTime()) ? null : d;
  }

  let dropoffLocation = '';
  let dropoffAt = null;

  const dropoffMatches = Array.from(
    text.matchAll(/Drop off\s+(\d{2}\.\d{2}\.\d{4})\s+(.+?)\s+(\d{1,2}:\d{2})/gi)
  );
  if (dropoffMatches.length > 0) {
    const m = dropoffMatches[dropoffMatches.length - 1];
    const [, dateStr, locRaw, timeStr] = m;
    const loc = String(locRaw).replace(/\s*(Rental office|Office|Airport)\s*$/i, '').trim();
    dropoffLocation = normalize(loc.replace(/[,;]\s*$/, ''));
    const [dd, mm, yyyy] = dateStr.split('.');
    const iso = `${yyyy}-${mm}-${dd}T${timeStr}:00`;
    const d = new Date(iso);
    dropoffAt = Number.isNaN(d.getTime()) ? null : d;
  }

  // Booking total
  let rentalAmount = null;
  let rentalCurrency = '';
  const bookingTotalMatch = text.match(/Booking total\s+([0-9]+(?:[.,][0-9]+)?)\s*([A-Za-z]{3}|[$€£])/i);
  if (bookingTotalMatch) {
    rentalAmount = parseNumberSmart(bookingTotalMatch[1]);
    rentalCurrency = normalizeCurrencyCode(bookingTotalMatch[2]);
  }

  // Paid advance (FIXED)
  let amountPaid = null;
  let paymentStatus = 'NOT_PAID';

  const paidBlockMatch = text.match(/Paid advance([\s\S]*?)(?=Booking total|$)/i);
  if (paidBlockMatch) {
    const block = paidBlockMatch[1];

    const amountRegex = /([0-9]+(?:[.,][0-9]+)?)\s*([A-Za-z]{3}|[$€£])/g;
    const candidates = [];
    let m;
    while ((m = amountRegex.exec(block)) !== null) {
      const amt = parseNumberSmart(m[1]);
      const cur = normalizeCurrencyCode((m[2] || '').trim());
      if (amt == null) continue;
      candidates.push({ amt, cur });
    }

    if (candidates.length) {
      // Prefer same currency as booking total, if we have it
      const sameCur = rentalCurrency
        ? candidates.filter((c) => c.cur === rentalCurrency)
        : candidates;

      const pool = sameCur.length ? sameCur : candidates;

      if (rentalAmount != null) {
        // 1) prepaid: amount ~= total
        // Localrent quirk: the paid-advance block can include the TOTAL as well as the actual prepaid.
        // So: prefer an amount UNDER total if it exists. Only treat it as prepaid if there's nothing under.

        const under = pool.filter((c) => c.amt > 0 && c.amt < rentalAmount - 0.01);
        if (under.length) {
          // advance: largest amount < total
          amountPaid = under.reduce((best, c) => (c.amt > best.amt ? c : best), under[0]).amt;
        } else {
          // only then consider it fully prepaid
          const prepaid = pool.find((c) => Math.abs(c.amt - rentalAmount) <= 0.01);
          if (prepaid) amountPaid = prepaid.amt;
          else {
            // fallback: smallest positive
            const pos = pool.filter((c) => c.amt > 0);
            if (pos.length) amountPaid = pos.reduce((best, c) => (c.amt < best.amt ? c : best), pos[0]).amt;
          }
        }

      } else {
        // No total known: pick largest positive (more likely meaningful than smallest)
        const pos = pool.filter((c) => c.amt > 0);
        if (pos.length) amountPaid = pos.reduce((best, c) => (c.amt > best.amt ? c : best), pos[0]).amt;
      }

      // If we still don’t have rentalCurrency, take it from the chosen candidate
      if (!rentalCurrency) {
        const chosen = pool.find((c) => c.amt === amountPaid);
        if (chosen?.cur) rentalCurrency = chosen.cur;
      }
    }
  }

  if (amountPaid != null && amountPaid > 0) {
    if (rentalAmount != null && amountPaid >= rentalAmount - 0.01) paymentStatus = 'PREPAID';
    else paymentStatus = 'PARTIALLY_PAID';
  }

  return {
    platform: 'local_rent',
    reservationRef,

    pickupLocation,
    dropoffLocation,
    pickupAt,
    dropoffAt,

    rentalAmount,
    rentalCurrency,

    depositAmount: null,
    depositCurrency: '',

    paymentStatus,
    amountPaid,
  };
}


function buildParsedFromEmail(email) {
  const source = detectSource(email);

  let parsed = {
    platform: source,
    reservationRef: '',
    customerName: '',
    phoneNumbers: [],
    pickupLocation: '',
    dropoffLocation: '',
    pickupAt: null,
    dropoffAt: null,
    flightNumber: '',
    rentalAmount: null,
    rentalCurrency: '',
    depositAmount: null,
    depositCurrency: '',

    paymentStatus: 'NOT_PAID',
    amountPaid: null,

    acceptUrl: email?.acceptUrl || '',
    declineUrl: email?.declineUrl || '',
  };

  if (source === 'economybookings') parsed = { ...parsed, ...parseBookingGroup(email) };
  else if (source === 'discovercars') parsed = { ...parsed, ...parseDiscoverCars(email) };
  else if (source === 'local_rent') parsed = { ...parsed, ...parseLocalRent(email) };

  return parsed;
}

function inboundToInboxItem(r) {
  const isBooking = r.type === 'PUBLIC_RESERVATION' || r.type === 'PARTNER_LEAD';
  const platform = isBooking ? 'LANDING_BOOKING' : 'LANDING_CONTACT';

  const subject = isBooking ? `Landing booking • ${r.name || 'Unknown'}` : `Landing contact • ${r.name || 'Unknown'}`;

  const payloadObj = safeJsonParseDeep(r.payload) || {};
  const payloadPrefill = extractInboundPayloadPrefill(payloadObj);
  const extrasPretty = formatExtras(payloadObj.extras);
  const flightNumber = payloadObj.flightNumber || payloadObj.flight || '';
  const insuranceCoverage = formatInsuranceCoverageStatus(payloadPrefill.insuranceIncluded);
  const insuranceAmountLine = formatPrefillMoney(
    payloadPrefill.insuranceAmount,
    payloadPrefill.insuranceCurrency || payloadPrefill.rentalCurrency
  );
  const rentalAmountLine = formatPrefillMoney(payloadPrefill.rentalAmount, payloadPrefill.rentalCurrency);
  const extrasAmountLine = formatPrefillMoney(payloadPrefill.extrasAmount, payloadPrefill.rentalCurrency);
  const quoteTotalLine = formatPrefillMoney(payloadPrefill.quoteTotal, payloadPrefill.rentalCurrency);
  const depositAmountLine = formatPrefillMoney(payloadPrefill.depositAmount, payloadPrefill.depositCurrency);
  const amountPaidLine = formatPrefillMoney(payloadPrefill.amountPaid, payloadPrefill.rentalCurrency);

  const babySeat = payloadObj.babyCarSeat != null ? Boolean(payloadObj.babyCarSeat) : null;

  const selectedCar =
    r.selectedCarName || r.selectedCarPlate
      ? `${r.selectedCarName || ''}${r.selectedCarPlate ? ` (${r.selectedCarPlate})` : ''}`.trim()
      : null;

  const lines = [
    'Customer',
    `Name: ${r.name || '-'}`,
    `Email: ${r.email || '-'}`,
    `Phone: ${r.phone || '-'}`,
    r.whatsapp ? `WhatsApp: ${r.whatsapp}` : null,
    r.country ? `Country: ${r.country}` : null,

    isBooking ? '' : null,

    isBooking ? 'Trip' : null,
    r.pickupLocation ? `Pickup: ${r.pickupLocation}` : null,
    r.dropoffLocation ? `Dropoff: ${r.dropoffLocation}` : null,
    r.start ? `Start: ${r.start}` : null,
    r.end ? `End: ${r.end}` : null,

    isBooking ? '' : null,

    isBooking ? 'Preferences' : null,
    r.transmissionPref ? `Transmission: ${r.transmissionPref}` : null,
    r.categoryPref ? `Category: ${r.categoryPref}` : null,

    isBooking ? '' : null,

    isBooking ? 'Extras' : null,
    flightNumber ? `Flight: ${flightNumber}` : null,
    extrasPretty ? `Extras: ${extrasPretty}` : null,
    insuranceCoverage ? `Insurance: ${insuranceCoverage}` : null,
    insuranceAmountLine ? `Insurance amount: ${insuranceAmountLine}` : null,
    babySeat != null ? `Baby seat: ${babySeat ? 'Yes' : 'No'}` : null,
    selectedCar ? `Selected car: ${selectedCar}` : null,

    isBooking ? '' : null,

    isBooking ? 'Pricing & payment' : null,
    rentalAmountLine ? `Rental total: ${rentalAmountLine}` : null,
    extrasAmountLine ? `Extras total: ${extrasAmountLine}` : null,
    quoteTotalLine ? `Quote total: ${quoteTotalLine}` : null,
    depositAmountLine ? `Deposit: ${depositAmountLine}` : null,
    payloadPrefill.paymentStatus ? `Payment status: ${formatReservationPaymentStatus(payloadPrefill.paymentStatus)}` : null,
    amountPaidLine ? `Amount paid: ${amountPaidLine}` : null,

    r.message ? '' : null,
    r.message ? `Message:\n${r.message}` : null,
  ].filter(Boolean);

  const isRead = r.status === 'CLOSED' || r.status === 'SPAM';

  return {
    __kind: 'inbound',
    id: r.id,
    CompanyId: r.CompanyId || '',
    senderEmail: r.email || '(no email)',
    subject,
    snippet: r.message || extrasPretty || insuranceCoverage || flightNumber || '',
    bodyText: lines.join('\n'),
    bodyHtml: null,
    platform,
    receivedAt: r.createdAt,
    isRead,

    assignedCarId: r.assignedCarId || null,
    assignedBySub: r.assignedBySub || null,
    assignedAt: r.assignedAt || null,

    __raw: r,
  };
}

/* ======================= Nice Alert (reusable hook) ======================= */
function useNiceAlert() {
  const [state, setState] = useState({
    open: false,
    variant: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback((opts) => {
    setState({
      open: true,
      variant: opts.variant || 'info',
      title: opts.title || '',
      message: opts.message || '',
    });
  }, []);

  const hideAlert = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  function Alert() {
    if (!state.open) return null;

    const palette = {
      info: {
        border: '#38bdf8',
        bg: '#ecfeff',
        title: '#0ea5e9',
        text: '#0f172a',
        badgeBg: '#e0f2fe',
        badgeText: '#0369a1',
        buttonBg: '#0ea5e9',
      },
      success: {
        border: '#16a34a',
        bg: '#ecfdf3',
        title: '#15803d',
        text: '#0f172a',
        badgeBg: '#dcfce7',
        badgeText: '#166534',
        buttonBg: '#16a34a',
      },
      warning: {
        border: '#f59e0b',
        bg: '#fffbeb',
        title: '#b45309',
        text: '#111827',
        badgeBg: '#fef3c7',
        badgeText: '#b45309',
        buttonBg: '#f59e0b',
      },
      error: {
        border: '#ef4444',
        bg: '#fef2f2',
        title: '#b91c1c',
        text: '#111827',
        badgeBg: '#fee2e2',
        badgeText: '#b91c1c',
        buttonBg: '#ef4444',
      },
    };

    const colors = palette[state.variant] || palette.info;

    const scrimStyle = {
      position: 'fixed',
      inset: 0,
      background: 'rgba(15,23,42,0.45)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '15vh',
      zIndex: 99999,
    };

    const boxStyle = {
      width: '100%',
      maxWidth: 420,
      borderRadius: 16,
      border: `1px solid ${colors.border}`,
      boxShadow: '0 20px 50px rgba(15,23,42,0.35)',
      background: colors.bg,
      padding: 16,
      boxSizing: 'border-box',
    };

    return (
      <div style={scrimStyle} onClick={hideAlert}>
        <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                padding: '3px 7px',
                borderRadius: 999,
                background: colors.badgeBg,
                color: colors.badgeText,
                border: `1px solid ${colors.border}33`,
              }}
            >
              {state.variant === 'error'
                ? 'Error'
                : state.variant === 'warning'
                  ? 'Warning'
                  : state.variant === 'success'
                    ? 'Success'
                    : 'Notice'}
            </span>
          </div>
          {state.title && (
            <h4 style={{ margin: '0 0 4px', fontSize: 16, color: colors.title }}>{state.title}</h4>
          )}
          {state.message && (
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: colors.text }}>{state.message}</p>
          )}
          <div style={{ marginTop: 14, textAlign: 'right' }}>
            <Button onClick={hideAlert} style={{ background: colors.buttonBg, borderRadius: 999, padding: '6px 14px', fontSize: 13 }}>
              OK
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return { Alert, showAlert, hideAlert };
}

/* ======================= Inbox Page ======================= */
export default function InboxPage() {
  const [q, setQ] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const [platformFilter, setPlatformFilter] = useState('ALL'); // ALL | discovercars | ...
  const [assignedFilter, setAssignedFilter] = useState('ALL'); // ALL | ASSIGNED | UNASSIGNED
  const [sortMode, setSortMode] = useState('NEWEST'); // NEWEST | OLDEST

  const { selectedCompanyId, loadingCompany } = useCountry();

  const [claims, setClaims] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCountryAdmin, setIsCountryAdmin] = useState(false);

  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [nextTokenInbound, setNextTokenInbound] = useState(null);
  const [useStringKeyCond, setUseStringKeyCond] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const [cars, setCars] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPrefill, setDrawerPrefill] = useState(null);
  const [drawerEmail, setDrawerEmail] = useState(null);
  const { setInboxCount } = useInboxBadge();
  const loadingRef = useRef(false);
  const [allDoneChecked, setAllDoneChecked] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { Alert, showAlert } = useNiceAlert();
  const isMobile = useIsMobile();

  const handleToggleDone = useCallback(
    async (row, done) => {
      try {
        if (row.__kind === 'inbound') {
          const newStatus = done ? 'CLOSED' : 'NEW';

          const res = await client.graphql({
            query: UpdateInboundRequest,
            variables: { input: { id: row.id, status: newStatus } },
            authMode: 'userPool',
          });

          const updated = res.data?.updateInboundRequest;
          if (!updated) return;

          setItems((prev) => {
            const next = prev.map((it) =>
              it.id === row.id && it.__kind === 'inbound'
                ? { ...it, isRead: done, assignedCarId: updated.assignedCarId, assignedAt: updated.assignedAt }
                : it
            );
            setInboxCount(next.filter((m) => !m?.isRead).length);
            return next;
          });

          return;
        }

        const res = await client.graphql({
          query: UpdateEmailMessage,
          variables: { input: { id: row.id, isRead: done } },
          authMode: 'userPool',
        });

        const updated = res.data?.updateEmailMessage;
        if (!updated) return;

        setItems((prev) => {
          const next = prev.map((it) => (it.id === updated.id && it.__kind === 'email' ? { ...it, ...updated } : it));
          setInboxCount(next.filter((m) => !m?.isRead).length);
          return next;
        });
      } catch (e) {
        console.error('Toggle done failed:', e);
        showAlert({
          variant: 'error',
          title: 'Failed to update item',
          message: 'Could not mark this item as done. Check logs.',
        });
      }
    },
    [showAlert, setInboxCount]
  );

  const handleToggleAllDone = useCallback(
    async (checked) => {
      if (!items.length) return;

      const target = items.filter((m) => m && Boolean(m.isRead) !== checked);
      if (!target.length) {
        setAllDoneChecked(checked);
        return;
      }

      try {
        const results = await Promise.all(
          target.map((row) => {
            if (row.__kind === 'inbound') {
              return client
                .graphql({
                  query: UpdateInboundRequest,
                  variables: { input: { id: row.id, status: checked ? 'CLOSED' : 'NEW' } },
                  authMode: 'userPool',
                })
                .then(() => ({ id: row.id }));
            }

            return client
              .graphql({
                query: UpdateEmailMessage,
                variables: { input: { id: row.id, isRead: checked } },
                authMode: 'userPool',
              })
              .then((res) => res.data?.updateEmailMessage || null);
          })
        );

        const succeededIds = new Set(results.filter(Boolean).map((r) => r.id));

        setItems((prev) => {
          const next = prev.map((it) => (it && succeededIds.has(it.id) ? { ...it, isRead: checked } : it));
          setInboxCount(next.filter((m) => !m?.isRead).length);
          setAllDoneChecked(next.length > 0 && next.every((m) => m?.isRead));
          return next;
        });
      } catch (e) {
        console.error('Toggle all done failed:', e);
        showAlert({
          variant: 'error',
          title: 'Failed to update emails',
          message: 'Could not mark all emails as done. Check logs.',
        });
      }
    },
    [items, setInboxCount, showAlert]
  );

  const canViewInbox = isAdmin || isCountryAdmin;
  const canEdit = canViewInbox;

  const pushAlert = useCallback(
    (opts) => {
      if (!opts) return;
      if (opts.type === 'confirm') {
        showAlert({
          variant: 'warning',
          title: opts.title || 'Conflicts detected',
          message: (opts.message || '') + ' Saving anyway from Inbox.',
        });
        if (typeof opts.onConfirm === 'function') opts.onConfirm();
        return;
      }
      const variant =
        opts.type === 'error' ? 'error' : opts.type === 'warning' ? 'warning' : opts.type === 'success' ? 'success' : 'info';
      showAlert({ variant, title: opts.title || '', message: opts.message || '' });
    },
    [showAlert]
  );

  useEffect(() => {
    if (!items.length) {
      setAllDoneChecked(false);
      return;
    }
    setAllDoneChecked(items.every((m) => m?.isRead));
  }, [items]);

  // Load Cognito claims once
  useEffect(() => {
    (async () => {
      const { tokens } = await fetchAuthSession();
      const p = tokens?.idToken?.payload || {};
      const CompanyId = p['custom:CompanyId'] ?? p['custom:companyId'] ?? null;
      const groups = p['cognito:groups'] || [];
      const sub = p.sub || null;

      const isAdminFlag = groups.includes('admin');
      const isCountryAdminFlag = groups.includes('country_admin');

      setClaims({ CompanyId, groups, sub });
      setIsAdmin(isAdminFlag);
      setIsCountryAdmin(isCountryAdminFlag);

      setAuthReady(Boolean(CompanyId) || isAdminFlag);
    })();
  }, []);

  const loadCars = useCallback(async () => {
    try {
      const res = await client.graphql({ query: ListCars, authMode: 'userPool' });
      const all = (res.data?.listCars?.items || []).filter(Boolean);
      const scoped = selectedCompanyId ? all.filter((c) => c.CompanyId === selectedCompanyId) : all;
      setCars(scoped);
    } catch (e) {
      console.error('Cars load failed:', e);
      setCars([]);
      showAlert({
        variant: 'error',
        title: 'Failed to load cars',
        message: 'Could not load the cars list from the backend.',
      });
    }
  }, [showAlert, selectedCompanyId]);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await client.graphql({ query: ListCompanies, authMode: 'userPool' });
      const all = (res.data?.listCompanies?.items || []).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(all);
    } catch (e) {
      console.error('Companies load failed:', e);
      setCompanies([]);
      showAlert({
        variant: 'error',
        title: 'Failed to load companies',
        message: 'Could not load the companies list from the backend.',
      });
    }
  }, [showAlert]);

  useEffect(() => {
    if (authReady && canViewInbox) {
      loadCars();
      loadCompanies();
    }
  }, [authReady, canViewInbox, loadCars, loadCompanies]);

  // ✅ FIX: actually use forceString to select DT vs STR query
  const runEmailsQuery = useCallback(
    async (forceString, token = null) => {
      const CompanyId = selectedCompanyId || claims?.CompanyId || 'root';
      if (!CompanyId) throw new Error('No CompanyId available for inbox query');

      const queryDoc = forceString ? EmailsByCompanyByReceived_STR : EmailsByCompanyByReceived_DT;

      const res = await client.graphql({
        query: queryDoc,
        variables: {
          CompanyId,
          receivedAt: { ge: isoDaysAgo(30) },
          sortDirection: 'DESC',
          limit: 50,
          nextToken: token,
        },
        authMode: 'userPool',
      });

      const root = res.data?.emailsByCompanyByReceived || {};
      return { items: root.items || [], nextToken: root.nextToken || null };
    },
    [selectedCompanyId, claims]
  );

  const runInboundQuery = useCallback(
    async (token = null) => {
      const CompanyId = selectedCompanyId || claims?.CompanyId || 'root';
      if (!CompanyId) throw new Error('No CompanyId available for inbound query');

      const res = await client.graphql({
        query: InboundByCompanyByCreatedAt,
        variables: {
          CompanyId,
          createdAt: { ge: isoDaysAgo(30) },
          sortDirection: 'DESC',
          limit: 50,
          nextToken: token,
        },
        authMode: 'userPool',
      });

      const root = res.data?.inboundByCompanyByCreatedAt || {};
      return { items: root.items || [], nextToken: root.nextToken || null };
    },
    [selectedCompanyId, claims]
  );

  const load = useCallback(async () => {
    if (!authReady || !canViewInbox) {
      setItems([]);
      setNextToken(null);
      setNextTokenInbound(null);
      setInitialLoading(false); // nothing to load in this state
      return;
    }
    if (loadingRef.current) return;

    loadingRef.current = true;
    setInitialLoading(true);

    try {
      // 1) Emails
      let emailData;
      try {
        emailData = await runEmailsQuery(useStringKeyCond, null);
      } catch (e) {
        const msg = String(e?.errors?.[0]?.message || e?.message || '');
        if (!useStringKeyCond && /Unknown type .*ModelAWSDateTimeKeyConditionInput/i.test(msg)) {
          const data2 = await runEmailsQuery(true, null);
          setUseStringKeyCond(true);
          emailData = data2;
        } else {
          throw e;
        }
      }

      // 2) Inbound requests
      const inboundData = await runInboundQuery(null);

      const inboundItems = (inboundData.items || []).filter(Boolean).map(inboundToInboxItem);
      const emailItems = (emailData.items || [])
        .filter(Boolean)
        .filter(shouldShowEmailInInbox)
        .map((x) => ({ __kind: 'email', ...x }));

      const merged = [...emailItems, ...inboundItems].sort((a, b) => {
        const da = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
        const db = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
        return db - da;
      });

      setItems(merged);
      setNextToken(emailData.nextToken);
      setNextTokenInbound(inboundData.nextToken);
      setExpanded(new Set());

      setInboxCount(merged.filter((m) => !m?.isRead).length);
    } catch (e) {
      console.error('Inbox load failed:', e);
      setItems([]);
      setNextToken(null);
      setNextTokenInbound(null);
      showAlert({
        variant: 'error',
        title: 'Failed to load inbox',
        message: 'Could not load emails + landing requests from the backend.',
      });
    } finally {
      loadingRef.current = false;
      setInitialLoading(false);
    }
  }, [authReady, canViewInbox, runEmailsQuery, runInboundQuery, useStringKeyCond, showAlert, setInboxCount]);

  const handleSyncNow = useCallback(async () => {
    if (!authReady || !canViewInbox) return;
    if (syncing) return;

    setSyncing(true);
    try {
      const res = await client.graphql({
        query: RunEmailIngest,
        authMode: 'userPool',
      });

      const payload = res.data?.runEmailIngest;
      if (!payload?.ok) throw new Error(payload?.message || 'Ingestion failed');

      showAlert({
        variant: 'info',
        title: 'Sync started',
        message: 'Gmail ingestion has been triggered. New emails will appear as soon as the Lambda finishes.',
      });

      setTimeout(() => load(), 5000);
    } catch (e) {
      console.error('runEmailIngest failed:', e);
      showAlert({
        variant: 'error',
        title: 'Sync failed',
        message: 'Could not trigger Gmail ingestion. Check CloudWatch logs for gmailIngestTrigger.',
      });
    } finally {
      setSyncing(false);
    }
  }, [authReady, canViewInbox, syncing, showAlert, load]);

  const loadMore = useCallback(async () => {
    if (!nextToken || loadingRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);

    try {
      const data = await runEmailsQuery(useStringKeyCond, nextToken);

      const moreEmails = (data.items || [])
        .filter(Boolean)
        .filter(shouldShowEmailInInbox)
        .map((x) => ({ __kind: 'email', ...x }));

      const merged = [...items, ...moreEmails]
        .filter(Boolean)
        .filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i)
        .sort((a, b) => new Date(b.receivedAt || 0).getTime() - new Date(a.receivedAt || 0).getTime());

      setItems(merged);
      setNextToken(data.nextToken);
      setInboxCount(merged.filter((m) => !m?.isRead).length);
    } catch (e) {
      console.error('Inbox loadMore failed:', e);
      showAlert({
        variant: 'error',
        title: 'Failed to load more emails',
        message: 'Could not fetch additional emails.',
      });
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [nextToken, items, runEmailsQuery, useStringKeyCond, showAlert, setInboxCount]);

  useEffect(() => {
    if (authReady && canViewInbox) load();
  }, [authReady, canViewInbox, load]);

  function toggleExpand(id) {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function companyName(id, list) {
    return (list || []).find((c) => c.id === id)?.name || id || '(unknown)';
  }
  function carName(carId) {
    const c = cars.find((x) => x.id === carId);
    return c ? (c.plate ? `${c.name} (${c.plate})` : c.name) : carId || '';
  }

  function getPlatformMeta(m) {
    const p = m.platform || '';

    if (p === 'LANDING_BOOKING') return { label: 'Landing booking', color: '#7c2d12', bg: '#ffedd5', title: 'Created from public booking form' };
    if (p === 'LANDING_CONTACT') return { label: 'Landing contact', color: '#4b5563', bg: '#e5e7eb', title: 'Created from public contact form' };
    if (p === 'discovercars') return { label: 'DiscoverCars', color: '#2563eb', bg: '#dbeafe', title: 'Imported from DiscoverCars email' };
    if (p === 'economybookings') return { label: 'EconomyBookings', color: '#0f766e', bg: '#ccfbf1', title: 'Imported from Booking Group / EconomyBookings email' };
    if (p === 'local_rent') return { label: 'Localrent', color: '#7c3aed', bg: '#ede9fe', title: 'Imported from Localrent email' };
    if (p === 'website') return { label: 'Website', color: '#166534', bg: '#e5f7da', title: 'Created from website booking' };
    if (!p) return null;

    return { label: p, color: '#6b7280', bg: '#e5e7eb', title: 'Other source' };
  }
  const visibleItems = useMemo(() => {
    const needle = q.trim().toLowerCase();

    const passesText = (m) => {
      if (!needle) return true;
      const hay =
        [
          m.subject,
          m.senderEmail,
          m.snippet,
          m.bodyText,
          // never search HTML directly (huge/noisy)
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
      return hay.includes(needle);
    };

    const passesUnread = (m) => (showUnreadOnly ? !m?.isRead : true);

    const passesPlatform = (m) => {
      if (platformFilter === 'ALL') return true;
      return String(m.platform || '').toLowerCase() === String(platformFilter).toLowerCase();
    };

    const passesAssigned = (m) => {
      if (assignedFilter === 'ALL') return true;
      const assigned = Boolean(m.assignedCarId);
      if (assignedFilter === 'ASSIGNED') return assigned;
      if (assignedFilter === 'UNASSIGNED') return !assigned;
      return true;
    };

    const out = (items || [])
      .filter(Boolean)
      .filter(passesText)
      .filter(passesUnread)
      .filter(passesPlatform)
      .filter(passesAssigned);

    out.sort((a, b) => {
      const da = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const db = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return sortMode === 'OLDEST' ? da - db : db - da;
    });

    return out;
  }, [items, q, showUnreadOnly, platformFilter, assignedFilter, sortMode]);

  const KNOWN_PLATFORMS = useMemo(
    () =>
      new Set([
        'website',
        'discovercars',
        'economybookings',
        'local_rent',
        'LANDING_BOOKING',
        'LANDING_CONTACT',
        'other',
      ]),
    []
  );

  function normalizePlatform(email, parsed) {
    const detected = parsed.platform || detectSource(email) || null;
    const stored = (email.platform || '').trim();

    if (stored && KNOWN_PLATFORMS.has(stored)) {
      if (stored === 'other' && detected && detected !== 'other') return detected;
      return stored;
    }

    if (stored && stored.includes('@')) return detected || 'other';
    return detected || 'other';
  }

  function openAssign(row) {
    if (!row) return;

    // Inbound requests -> prefer your dedicated parser util for website message (more consistent)
    if (row.__kind === 'inbound') {
      const r = row.__raw || {};
      const payloadObj = safeJsonParseDeep(r.payload) || {};

      const isBooking = row.platform === 'LANDING_BOOKING';
      const base = {
        companyId: row.CompanyId || selectedCompanyId || claims?.CompanyId || '',
        reservationRef: row.id,
        customerName: r.name || '',
        senderEmail: r.email || '',
        phoneNumbers: [r.phone, r.whatsapp].filter(Boolean),
        platform: 'website',
        title: row.subject || (isBooking ? `Landing booking • ${r.name || 'Unknown'}` : 'Website contact'),
      };

      let prefill;

      if (isBooking) {
        // Build a website-lead style message and let the util parse it.
        const selectedCarLine =
          r.selectedCarName || r.selectedCarPlate
            ? `${r.selectedCarName || ''}${r.selectedCarPlate ? ` · ${r.selectedCarPlate}` : ''}`.trim()
            : '';

        const constructed = [
          `Name: ${r.name || ''}`,
          `Email: ${r.email || ''}`,
          `Phone: ${r.phone || ''}`,
          r.whatsapp ? `WhatsApp: ${r.whatsapp}` : '',
          `Start: ${r.start || ''}`,
          `End: ${r.end || ''}`,
          `Pickup: ${r.pickupLocation || ''}`,
          `Dropoff: ${r.dropoffLocation || ''}`,
          selectedCarLine ? `Selected car: ${selectedCarLine}` : '',
          payloadObj.flightNumber || payloadObj.flight ? `Flight: ${payloadObj.flightNumber || payloadObj.flight}` : '',
          payloadObj.extras != null ? `Extras: ${typeof payloadObj.extras === 'string' ? payloadObj.extras : JSON.stringify(payloadObj.extras)}` : '',
        ]
          .filter(Boolean)
          .join('\n');

        const parsed = parseWebsiteMessageToPrefill(constructed, cars);
        const inboundExtras = mapInboundExtrasToReservationExtras(payloadObj.extras);
        const hasBabySeatToken = inboundExtras.some((token) => {
          const parsedSeat = parseSeatExtraCountToken(token);
          return String(token || '').trim().toLowerCase() === 'baby_seat' || parsedSeat?.token === 'baby_seat';
        });
        if (payloadObj.babyCarSeat && !hasBabySeatToken) inboundExtras.push('baby_seat');
        const mergedExtras = Array.from(new Set([...(parsed.extras || []), ...inboundExtras]));
        const payloadPrefill = extractInboundPayloadPrefill(payloadObj);

        prefill = {
          ...base,
          ...parsed,
          ...payloadPrefill,
          carId: r.selectedCarId || parsed.carId || '',
          pickupAt: r.start ? new Date(r.start) : parsed.pickupAt,
          dropoffAt: r.end ? new Date(r.end) : parsed.dropoffAt,
          pickupLocation: r.pickupLocation || parsed.pickupLocation || '',
          dropoffLocation: r.dropoffLocation || parsed.dropoffLocation || '',
          extras: mergedExtras,
          notes: (r.message || '').trim() || parsed.notes || '',
          transmissionPref: r.transmissionPref || undefined,
          categoryPref: r.categoryPref || undefined,
        };
      } else {
        prefill = {
          ...base,
          carId: '',
          pickupAt: null,
          dropoffAt: null,
          pickupLocation: '',
          dropoffLocation: '',
          notes: r.message || '',
        };
      }

      setDrawerPrefill(prefill);
      setDrawerEmail(row);
      setDrawerOpen(true);
      return;
    }

    // Emails
    const parsed = buildParsedFromEmail(row);
    const platform = normalizePlatform(row, parsed);
    const companyId = row.CompanyId || selectedCompanyId || claims?.CompanyId || '';
    const company = (companies || []).find((c) => c.id === companyId) || null;
    const companyTz = inferTimeZoneForCompany(company, 'UTC');
    const pickupAtInCompanyTz = asWallClockInTimeZoneToIso(parsed.pickupAt, companyTz);
    const dropoffAtInCompanyTz = asWallClockInTimeZoneToIso(parsed.dropoffAt, companyTz);

    const prefill = {
      companyId,
      carId: '',
      pickupAt: pickupAtInCompanyTz || parsed.pickupAt,
      dropoffAt: dropoffAtInCompanyTz || parsed.dropoffAt,
      pickupLocation: parsed.pickupLocation || '',
      dropoffLocation: parsed.dropoffLocation || '',
      reservationRef: parsed.reservationRef || row.gmailMessageId || '',
      customerName: parsed.customerName || '',
      flightNumber: parsed.flightNumber || '',
      phoneNumbers: parsed.phoneNumbers || [],
      depositAmount: parsed.depositAmount ?? null,
      depositCurrency: parsed.depositCurrency || '',
      rentalAmount: parsed.rentalAmount ?? null,
      rentalCurrency: parsed.rentalCurrency || '',
      paymentStatus: parsed.paymentStatus || 'NOT_PAID',
      amountPaid: parsed.amountPaid ?? null,
      platform,
      title: row.subject || '',
      notes: '',
      acceptUrl: parsed.acceptUrl || row.acceptUrl || '',
      declineUrl: parsed.declineUrl || row.declineUrl || '',
      senderEmail: row.senderEmail || '',
    };

    setDrawerPrefill(prefill);
    setDrawerEmail(row);
    setDrawerOpen(true);
  }

  const handleDrawerSaved = useCallback(
    async (info) => {
      if (!drawerEmail) return;
      if (!info || info.kind !== 'reservation') return;
      const carId = info.carId;
      if (!carId) return;

      try {
        if (drawerEmail.__kind === 'inbound') {
          await client.graphql({
            query: UpdateInboundRequest,
            variables: {
              input: {
                id: drawerEmail.id,
                assignedCarId: carId,
                assignedBySub: claims?.sub || null,
                assignedAt: new Date().toISOString(),
                status: 'CLOSED',
              },
            },
            authMode: 'userPool',
          });

          showAlert({
            variant: 'success',
            title: 'Reservation created',
            message: 'Inbound request assigned and closed.',
          });

          load();
          return;
        }

        await client.graphql({
          query: AssignEmailToCar,
          variables: { emailId: drawerEmail.id, carId },
          authMode: 'userPool',
        });

        const parsed = buildParsedFromEmail(drawerEmail);
        let newPlatform = normalizePlatform(drawerEmail, parsed);
        if (info.platform) newPlatform = info.platform;

        await client.graphql({
          query: UpdateEmailMessage,
          variables: {
            input: {
              id: drawerEmail.id,
              isRead: true,
              assignedCarId: carId,
              assignedBySub: claims?.sub || null,
              assignedAt: new Date().toISOString(),
              platform: newPlatform,
            },
          },
          authMode: 'userPool',
        });

        showAlert({
          variant: 'success',
          title: 'Reservation linked',
          message: 'Reservation created and email linked to the car.',
        });

        load();
      } catch (e) {
        console.error('After reservation saved failed:', e);
        showAlert({
          variant: 'error',
          title: 'Post-save failed',
          message: 'Reservation created, but closing/assigning the source item failed. Check logs.',
        });
      } finally {
        setDrawerOpen(false);
        setDrawerEmail(null);
        setDrawerPrefill(null);
      }
    },
    [drawerEmail, claims?.sub, load, showAlert]
  );

  if (!authReady) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading auth…</div>
      </div>
    );
  }

  if (!canViewInbox) {
    return (
      <div style={{ padding: 24 }}>
        <b>Not authorized.</b> Inbox is only for global admins and country admins.
      </div>
    );
  }

  if (loadingCompany) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading company…</div>
      </div>
    );
  }

  if (!selectedCompanyId) {
    return (
      <div style={{ padding: 24 }}>
        <b>No active company.</b>
        <p style={{ fontSize: 13, color: '#6b7280' }}>
          No company resolved for the current country. Check your Company.country values or the country toggle.
        </p>
      </div>
    );
  }

  function Pill({ children, color = '#198754', bg = '#d1e7dd', title }) {
    return (
      <span
        title={title}
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          fontSize: 12,
          borderRadius: 999,
          color,
          background: bg,
          border: `1px solid ${color}22`,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <div className="IxPage">
      <InboxStyles />

      <div className="IxContainer" style={{ maxWidth: isMobile ? '100%' : 1120 }}>
        {/* Header */}
        <header className="IxHeader">
          <div>
            <h3 className="IxTitle">Inbox</h3>
            <p className="IxDesc">
              Platform emails and landing form requests, with quick assignment into reservations.
            </p>
          </div>
          <span className="IxRole">Admin / Country admin</span>
        </header>

        {/* Toolbar */}
        <section className="IxCard">
          <div className="IxToolbar">
            <div className="IxInput" title="Search subject, sender, snippet, body text">
              <span style={{ color: 'rgba(15,23,42,.55)', fontWeight: 900 }}>Search</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, email, reservation ref, location…"
              />
            </div>

            <div className="IxSelect" title="Platform filter">
              <span style={{ color: 'rgba(15,23,42,.55)', fontWeight: 900 }}>Platform</span>
              <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
                <option value="ALL">All</option>
                <option value="discovercars">DiscoverCars</option>
                <option value="economybookings">EconomyBookings</option>
                <option value="local_rent">Localrent</option>
                <option value="LANDING_BOOKING">Landing booking</option>
                <option value="LANDING_CONTACT">Landing contact</option>
              </select>
            </div>

            <div className="IxSelect" title="Assigned filter">
              <span style={{ color: 'rgba(15,23,42,.55)', fontWeight: 900 }}>Assigned</span>
              <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}>
                <option value="ALL">All</option>
                <option value="UNASSIGNED">Unassigned</option>
                <option value="ASSIGNED">Assigned</option>
              </select>
            </div>

            <div className="IxSelect" title="Sort">
              <span style={{ color: 'rgba(15,23,42,.55)', fontWeight: 900 }}>Sort</span>
              <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                <option value="NEWEST">Newest</option>
                <option value="OLDEST">Oldest</option>
              </select>
            </div>

            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 10px',
                borderRadius: 14,
                border: '1px solid rgba(17,24,39,.14)',
                background: 'rgba(255,255,255,.72)',
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: 13,
                fontWeight: 900,
                color: 'rgba(15,23,42,.78)',
              }}
              title="Toggle unread-only view"
            >
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Unread only
            </label>

            <Button
              onClick={handleSyncNow}
              disabled={initialLoading || syncing}
              style={{ background: '#111827', color: 'white' }}
              title="Trigger Gmail ingestion Lambda"
            >
              {syncing ? 'Syncing…' : 'Sync Gmail'}
            </Button>

            <Button
              onClick={load}
              disabled={initialLoading}
              style={{ background: 'white' }}
              title="Reload from DynamoDB only"
            >
              Refresh
            </Button>

            <div className="IxMeta">
              Showing <b>{visibleItems.length}</b> / {items.length}
              {nextToken ? ' • more emails available' : ''}
            </div>
          </div>

          <div className="IxListHead">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(15,23,42,.72)', fontWeight: 900 }}>
              <input
                type="checkbox"
                checked={items.length > 0 && items.every((m) => m?.isRead)}
                onChange={(e) => {
                  // only apply to what the user is currently looking at
                  const checked = e.target.checked;
                  const targetIds = new Set(visibleItems.map(x => x.id));
                  const scoped = items.filter(x => targetIds.has(x.id));
                  // temporarily swap items into the handler logic
                  // simplest: run per row
                  Promise.all(scoped.map(row => handleToggleDone(row, checked)));
                }}
                style={{ cursor: 'pointer' }}
              />
              Mark all as done
            </label>

            <div style={{ fontSize: 12, color: 'rgba(15,23,42,.55)' }}>
              Tip: keep “Unread only” on and burn the queue down.
            </div>
          </div>

          {/* List */}
          <div className="IxList">
            {initialLoading ? (
              <div style={{ fontSize: 13, color: 'rgba(15,23,42,.55)', padding: 6 }}>Loading inbox…</div>
            ) : visibleItems.length === 0 ? (
              <div style={{ fontSize: 13, color: 'rgba(15,23,42,.55)', padding: 6 }}>
                No items match the current filters.
              </div>
            ) : null}

            {visibleItems.map((m) => {
              const isOpenCard = expanded.has(m.id);
              const isDone = Boolean(m.isRead);
              const assigned = Boolean(m.assignedCarId);

              const meta = getPlatformMeta(m);
              const onKey = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(m.id);
                }
              };
              const co = companies.find((c) => c.id === m.CompanyId) || null;
              const tz = inferTimeZoneForCompany(co);

              return (
                <div
                  key={m.id}
                  className={`IxRow ${isDone ? 'IxRowDone' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpenCard}
                  onClick={() => toggleExpand(m.id)}
                  onKeyDown={onKey}
                >
                  <div className="IxRowTop">
                    <div style={{ minWidth: 0, width: '100%' }}>
                      <div className={`IxSubject ${!isDone ? 'IxSubjectUnread' : ''}`}>
                        {m.subject || '(no subject)'}
                      </div>

                      <div className="IxSubline">
                        <span>{m.senderEmail}</span>
                        <span>•</span>
                        <span>{m.receivedAt ? formatDateTimeDMY(m.receivedAt, tz) : '(no date)'}</span>

                        <span>•</span>
                        <span>{companyName(m.CompanyId, companies)}</span>

                        {meta && (
                          <span className="IxPill" title={meta.title} style={{ background: meta.bg, color: meta.color, borderColor: `${meta.color}22` }}>
                            {meta.label}
                          </span>
                        )}

                        {assigned ? (
                          <span
                            className="IxPill"
                            title={m.assignedAt ? `Assigned at ${formatDateTimeDMY(m.assignedAt, tz)}` : 'Assigned'}
                          >
                            Assigned: {carName(m.assignedCarId)}
                          </span>
                        ) : (
                          <span className="IxPill" style={{ color: 'rgba(15,23,42,.55)' }}>Unassigned</span>
                        )}
                      </div>
                    </div>

                    <div className="IxActions" onClick={(e) => e.stopPropagation()}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(15,23,42,.70)', fontWeight: 900, userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={isDone}
                          onChange={(e) => handleToggleDone(m, e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        Done
                      </label>

                      <button
                        className="IxMiniBtn"
                        onClick={() => toggleExpand(m.id)}
                        title={isOpenCard ? 'Close' : 'Open'}
                      >
                        {isOpenCard ? 'Close' : 'Open'}
                      </button>

                      <button
                        className="IxMiniBtn IxMiniBtnPrimary"
                        onClick={() => openAssign(m)}
                        title="Assign to reservation"
                      >
                        Assign
                      </button>
                    </div>
                  </div>

                  {isOpenCard && (
                    <div className="IxBody" onClick={(e) => e.stopPropagation()}>
                      <div className="IxContentBox">
                        {/* Inbound requests: structured layout */}
                        {m.__kind === 'inbound' ? (() => {
                          const r = m.__raw || {};
                          const payloadObj = safeJsonParseDeep(r.payload) || {};
                          const payloadPrefill = extractInboundPayloadPrefill(payloadObj);
                          const insuranceCoverage = formatInsuranceCoverageStatus(payloadPrefill.insuranceIncluded);
                          const insuranceAmountLine = formatPrefillMoney(
                            payloadPrefill.insuranceAmount,
                            payloadPrefill.insuranceCurrency || payloadPrefill.rentalCurrency
                          );
                          const rentalAmountLine = formatPrefillMoney(
                            payloadPrefill.rentalAmount,
                            payloadPrefill.rentalCurrency
                          );
                          const extrasAmountLine = formatPrefillMoney(
                            payloadPrefill.extrasAmount,
                            payloadPrefill.rentalCurrency
                          );
                          const quoteTotalLine = formatPrefillMoney(
                            payloadPrefill.quoteTotal,
                            payloadPrefill.rentalCurrency
                          );
                          const depositAmountLine = formatPrefillMoney(
                            payloadPrefill.depositAmount,
                            payloadPrefill.depositCurrency
                          );
                          const amountPaidLine = formatPrefillMoney(
                            payloadPrefill.amountPaid,
                            payloadPrefill.rentalCurrency
                          );
                          const hasPricingDetails = Boolean(
                            insuranceCoverage ||
                              insuranceAmountLine ||
                              rentalAmountLine ||
                              extrasAmountLine ||
                              quoteTotalLine ||
                              depositAmountLine ||
                              amountPaidLine ||
                              payloadPrefill.paymentStatus
                          );
                          const co = companies.find((c) => c.id === m.CompanyId) || null;
                          const tz = inferTimeZoneForCompany(co);

                          const isBooking = r.type === 'PUBLIC_RESERVATION' || r.type === 'PARTNER_LEAD';
                          const selectedCar =
                            r.selectedCarName || r.selectedCarPlate
                              ? `${r.selectedCarName || ''}${r.selectedCarPlate ? ` (${r.selectedCarPlate})` : ''}`.trim()
                              : '';

                          return (
                            <div style={{ display: 'grid', gap: 10 }}>
                              <Section title="Customer">
                                <KV k="Name" v={r.name} />
                                <KV k="Email" v={r.email} />
                                <KV k="Phone" v={r.phone} />
                                <KV k="WhatsApp" v={r.whatsapp} />
                                <KV k="Country" v={r.country} />
                                <KV k="Status" v={r.status} />
                                <KV k="Created" v={r.createdAt ? formatDateTimeDMY(r.createdAt, tz) : ''} />
                                <KV k="Updated" v={r.updatedAt ? formatDateTimeDMY(r.updatedAt, tz) : ''} />
                              </Section>

                              {isBooking && (
                                <Section title="Trip">
                                  <KV k="Pickup" v={r.pickupLocation} />
                                  <KV k="Dropoff" v={r.dropoffLocation} />
                                  <KV k="Start" v={r.start ? formatDateTimeDMY(r.start, tz) : r.start} />
                                  <KV k="End" v={r.end ? formatDateTimeDMY(r.end, tz) : r.end} />
                                </Section>
                              )}

                              {isBooking && (
                                <Section title="Preferences">
                                  <KV k="Transmission" v={r.transmissionPref} />
                                  <KV k="Category" v={r.categoryPref} />
                                </Section>
                              )}

                              {isBooking && (
                                <Section title="Extras">
                                  <KV k="Flight" v={payloadObj.flightNumber || payloadObj.flight || ''} />
                                  <KV k="Extras" v={formatExtras(payloadObj.extras)} />
                                  <KV k="Insurance" v={insuranceCoverage} />
                                  <KV k="Insurance amount" v={insuranceAmountLine} />
                                  <KV k="Baby seat" v={payloadObj.babyCarSeat != null ? (payloadObj.babyCarSeat ? 'Yes' : 'No') : ''} />
                                  <KV k="Selected car" v={selectedCar} />
                                </Section>
                              )}

                              {isBooking && hasPricingDetails && (
                                <Section title="Pricing & Payment">
                                  <KV k="Rental total" v={rentalAmountLine} />
                                  <KV k="Extras total" v={extrasAmountLine} />
                                  <KV k="Quote total" v={quoteTotalLine} />
                                  <KV k="Deposit" v={depositAmountLine} />
                                  <KV k="Payment status" v={formatReservationPaymentStatus(payloadPrefill.paymentStatus)} />
                                  <KV k="Amount paid" v={amountPaidLine} />
                                </Section>
                              )}

                              {r.message ? (
                                <Section title="Message">
                                  <div style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(15,23,42,.88)', whiteSpace: 'pre-wrap' }}>
                                    {r.message}
                                  </div>
                                </Section>
                              ) : null}


                            </div>
                          );
                        })() : null}

                        {/* Emails: keep HTML rendering, but make plain text prettier */}
                        {m.__kind !== 'inbound' ? (
                          m.bodyHtml ? (
                            <div style={{ fontSize: 13, color: 'rgba(15,23,42,.88)', lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: m.bodyHtml }} />
                          ) : m.bodyText ? (
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, color: 'rgba(15,23,42,.88)', lineHeight: 1.55 }}>
                              {m.bodyText}
                            </div>
                          ) : m.snippet ? (
                            <div style={{ fontSize: 13, color: 'rgba(15,23,42,.82)' }}>{m.snippet}</div>
                          ) : (
                            <div style={{ fontSize: 13, color: 'rgba(15,23,42,.45)', fontStyle: 'italic' }}>(No content available)</div>
                          )
                        ) : null}
                      </div>
                    </div>
                  )}

                </div>
              );
            })}

            {nextToken && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 14px' }}>
                <Button onClick={loadMore} disabled={loadingMore || initialLoading}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        </section>

        <ReservationDrawer
          open={drawerOpen}
          mode="create"
          initial={null}
          prefill={drawerPrefill}
          companies={companies}
          cars={cars}
          claims={claims}
          useStringKeyCond={useStringKeyCond}
          activeCompanyId={selectedCompanyId}
          readOnly={!canEdit}
          onClose={() => {
            setDrawerOpen(false);
            setDrawerEmail(null);
            setDrawerPrefill(null);
          }}
          onSaved={handleDrawerSaved}
          pushAlert={pushAlert}
        />
      </div>

      <Alert />
    </div>
  );

}
